import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { Field, Label } from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { StripePaymentForm } from "@/components/stripe-payment-form";
import { orpc } from "@/orpc/client";

interface AddPaymentMethodDialogProps {
	isOpen: boolean;
	onClose: () => void;
	successRedirect: string | null;
}

export function AddPaymentMethodDialog({
	isOpen,
	onClose,
	successRedirect,
}: AddPaymentMethodDialogProps) {
	const queryClient = useQueryClient();
	const [name, setName] = useState("");
	const [clientSecret, setClientSecret] = useState<string | null>(null);
	const [entityId, setEntityId] = useState<string | null>(null);

	const { mutate: createEntity, isPending: isCreating } = useMutation(
		orpc.billingEntities.create.mutationOptions({
			onSuccess: (data) => {
				setClientSecret(data.clientSecret);
				setEntityId(data.billingEntity.id);
			},
			onError: (error) => {
				toast.error(`Failed to create: ${error.message}`);
			},
		}),
	);

	const { mutateAsync: confirmPaymentMethodAsync } = useMutation(
		orpc.billingEntities.confirmPaymentMethod.mutationOptions(),
	);

	const handleStartSetup = (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;
		createEntity({ name: name.trim() });
	};

	const handleClose = () => {
		setName("");
		setClientSecret(null);
		setEntityId(null);
		onClose();
	};

	const handleSuccess = () => {
		queryClient.invalidateQueries({
			queryKey: orpc.billingEntities.list.key(),
		});
		toast.success("Payment method added!");
		handleClose();
		if (successRedirect) {
			window.location.href = successRedirect;
		}
	};

	return (
		<Dialog open={isOpen} onClose={handleClose}>
			<DialogTitle>Add Payment Method</DialogTitle>
			<DialogDescription>
				{clientSecret
					? "Enter your card details to save this payment method."
					: "Give this payment method a name, then enter your card details."}
			</DialogDescription>

			{!clientSecret ? (
				<form onSubmit={handleStartSetup}>
					<DialogBody>
						<Field>
							<Label>Name</Label>
							<Input
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="e.g., Company Card"
								autoFocus
							/>
						</Field>
					</DialogBody>
					<DialogActions>
						<Button plain onClick={handleClose} type="button">
							Cancel
						</Button>
						<Button type="submit" disabled={isCreating || !name.trim()}>
							{isCreating ? "Setting up..." : "Continue"}
						</Button>
					</DialogActions>
				</form>
			) : (
				entityId && (
					<StripePaymentForm
						clientSecret={clientSecret}
						onSubmit={async ({ stripe, elements }) => {
							const { error: submitError } = await elements.submit();
							if (submitError)
								return submitError.message ?? "An error occurred";

							const { error: confirmError, setupIntent } =
								await stripe.confirmSetup({
									elements,
									confirmParams: {
										return_url: window.location.href,
									},
									redirect: "if_required",
								});

							if (confirmError)
								return confirmError.message ?? "Failed to confirm setup";

							if (setupIntent?.status !== "succeeded") {
								return "Setup was not completed. Please try again.";
							}

							try {
								await confirmPaymentMethodAsync({ id: entityId });
								handleSuccess();
							} catch (err) {
								return err instanceof Error
									? err.message
									: "Failed to confirm payment method";
							}

							return undefined;
						}}
					>
						{({ paymentElement, error, isProcessing, isReady }) => (
							<>
								<DialogBody>
									<div className="space-y-4">
										{paymentElement}
										{error}
									</div>
								</DialogBody>
								<DialogActions>
									<Button
										plain
										onClick={handleClose}
										type="button"
										disabled={isProcessing}
									>
										Cancel
									</Button>
									<Button type="submit" disabled={!isReady || isProcessing}>
										{isProcessing ? "Processing..." : "Save Card"}
									</Button>
								</DialogActions>
							</>
						)}
					</StripePaymentForm>
				)
			)}
		</Dialog>
	);
}
